import { useEffect, useRef, useState } from 'react';
import ProductColorBox from './ProductColorBox';
import { useDispatch, useSelector } from 'react-redux';
import { camelCaseToReadable, formatDescription } from '../../../lib/utils/utils';
import ProductSizeBox from './ProductSizeBox';
import { setPrices } from '../../../Features/inventoryManagementSlice';
import { getCategoriesForStore, getCollectionForCategory, getCustomCollectionsForStore } from '../../../lib/utils/apiCalls';
import { Link } from 'react-router-dom';

function ProductDetails() {
    const dispatch = useDispatch();
    const prevSizeRef = useRef();
    const prevColorRef = useRef();

    const [categoriesList, setCategoriesList] = useState([]);
    const [customCollectionsList, setCustomCollectionsList] = useState([]);
    const [collectionsList, setCollectionsList] = useState([]);

    const activeProduct = useSelector((state) => state.inventoryManagement.activeProduct.activeProduct);
    const { activeColorVariant: { color }, activeSize, sizeList, prices: { mrp, sp, discount } } =
        useSelector((state) => state.inventoryManagement.variantInfo);

    const [displayFullDescription, setDisplayFullDescription] = useState(false);
    const [colors, setColors] = useState([]);

    const handleDescriptionLength = () => {
        setDisplayFullDescription(!displayFullDescription);
    };

    // Color list generator (handles no-color catalogs gracefully)
    useEffect(() => {
    const generateColors = () => {
        if (!activeProduct || !activeProduct.product) return;

        const product = activeProduct.product;
        const variety = activeProduct.variety || [];

        const unique = [];
        const seen = new Set();

        // If variants exist, only add non-empty colors
        if (Array.isArray(variety) && variety.length > 0) {
            variety.forEach((v) => {
                const colorValue = (v.VarientColor ?? '').toString().trim();
                if (colorValue && !seen.has(colorValue)) {
                    seen.add(colorValue);
                    unique.push({
                        id: v.id,
                        color: colorValue,
                        imageList: v.VarientImgUrl
                    });
                }
            });
        } else {
            // If no variety, add only if product color is valid
            const colorValue = (product.productSpecs?.color ?? '').toString().trim();
            if (colorValue) {
                unique.push({
                    id: product.id,
                    color: colorValue,
                    imageList: product.productImgUrls || []
                });
            }
        }

        setColors(unique);
    };

    if (Object.keys(activeProduct || {}).length > 0) generateColors();
}, [activeProduct]);

    // Pricing calculator: works for size-only and size+color catalogs
    const calculateAndSetPrices = (size, selectedColor, product) => {
        if (!size || !product) return;

        const base = product?.product ?? {};
        const variants = product?.variety ?? [];

        const norm = (v) => (v ?? '').toString().trim();

        const baseSize = norm(base.productSpecs?.size);
        const baseColor = norm(base.productSpecs?.color);
        const selColor = norm(selectedColor);
        const selSize = norm(size);

        const hasAnyColorDimension =
            norm(baseColor) !== '' || variants.some((v) => norm(v.VarientColor) !== '');

        const pickAndDispatch = (mrpIn, spIn) => {
            const safeMrp = Number(mrpIn) || 0;
            const safeSp = Number(spIn) || 0;
            const disc = safeMrp > 0 ? Math.round(((safeMrp - safeSp) / safeMrp) * 100) : 0;
            dispatch(setPrices({ mrp: safeMrp, sp: safeSp, discount: disc }));
        };

        if (!hasAnyColorDimension) {
            // Size-only catalog
            if (selSize === baseSize) {
                pickAndDispatch(base.productMRP, base.productSellingPrice);
                return;
            }
            const hit = variants.find((v) => norm(v.VarientSize) === selSize);
            if (hit) pickAndDispatch(hit.VarientMrp, hit.VarientPrice);
            return;
        }

        // Color dimension exists
        if (selSize === baseSize && selColor === baseColor) {
            pickAndDispatch(base.productMRP, base.productSellingPrice);
            return;
        }

        const hit = variants.find(
            (v) => norm(v.VarientSize) === selSize && norm(v.VarientColor) === selColor
        );

        if (hit) {
            pickAndDispatch(hit.VarientMrp, hit.VarientPrice);
        } else {
            // Fallback: size-only match within variants
            const sizeOnly = variants.find((v) => norm(v.VarientSize) === selSize);
            if (sizeOnly) pickAndDispatch(sizeOnly.VarientMrp, sizeOnly.VarientPrice);
        }
    };

    useEffect(() => {
        if (!activeProduct || !activeSize) return;

        // Recalculate prices whenever product, size, or color changes
        calculateAndSetPrices(activeSize, color, activeProduct);

        const { categoryName, customCollectionName } = activeProduct || {};

        if (!categoryName) {
            setCategoriesList([]);
            return;
        }

        if (!customCollectionName) {
            setCustomCollectionsList([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const res = await getCategoriesForStore();
                const all = res?.category ?? [];

                // match by inclusion, case-insensitive
                const needle = String(categoryName).toLowerCase();
                const matches = all.filter(
                    (c) => needle.includes(String(c?.categoryName ?? '').toLowerCase())
                );

                if (!cancelled) {
                    setCategoriesList(matches);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setCategoriesList([]);
            }
        })();

        (async () => {
            try {
                const res = await getCustomCollectionsForStore();
                const all = res?.collection ?? [];

                // match by inclusion, case-insensitive
                const needle = String(customCollectionName).toLowerCase();
                const matches = all.filter(
                    (c) => needle.includes(String(c?.CollectionName ?? '').toLowerCase())
                );

                if (!cancelled) {
                    setCustomCollectionsList(matches);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setCustomCollectionsList([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [activeProduct]);

    // console.log(customCollectionsList);

    useEffect(() => {
        if (!activeProduct) return;

        const { collectionName } = activeProduct || {};
        if (!collectionName) {
            setCollectionsList([]);
            return;
        }

        const includedNames = new Set(
            (Array.isArray(collectionName)
                ? collectionName
                : String(collectionName).split(','))
                .map(s => s.trim().toLowerCase())
                .filter(Boolean)
        );

        if (includedNames.size === 0) {
            setCollectionsList([]);
            return;
        }

        if (!Array.isArray(categoriesList) || categoriesList.length === 0) {
            setCollectionsList([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const results = await Promise.allSettled(
                    categoriesList.map(cat => getCollectionForCategory(cat.id))
                );

                const allCollections = results.flatMap(r =>
                    r.status === 'fulfilled' ? (r.value?.collection ?? []) : []
                );

                const seen = new Set();
                const deduped = [];
                for (const c of allCollections) {
                    const key =
                        c?.id ??
                        c?.CollectionId ??
                        `${String(c?.CollectionName ?? '').toLowerCase()}::${String(c?.categoryId ?? '')}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        deduped.push(c);
                    }
                }

                const filtered = deduped.filter(c =>
                    includedNames.has(String(c?.CollectionName ?? '').toLowerCase())
                );

                if (!cancelled) {
                    setCollectionsList(filtered);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setCollectionsList([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [activeProduct, categoriesList]);

    // Update price when size/color changes (size is sufficient)
    useEffect(() => {
        const prevSize = prevSizeRef.current;
        const prevColor = prevColorRef.current;

        const sizeChanged = prevSize !== undefined && prevSize !== activeSize;
        const colorChanged = prevColor !== undefined && prevColor !== color;

        if ((sizeChanged || colorChanged) && activeSize && Object.keys(activeProduct || {}).length > 0) {
            calculateAndSetPrices(activeSize, color, activeProduct);
        }

        prevSizeRef.current = activeSize;
        prevColorRef.current = color;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSize, color, dispatch]);

    return (
        <div className="w-[63%] flex flex-col gap-3">
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1 font-semibold">
                        <p className="text-base text-[#666] capitalize">
                            {activeProduct?.product?.productType}
                            {activeProduct?.product?.productCategory && (
                                <span className="text-[#999]"> ({activeProduct.product.productCategory})</span>
                            )}
                        </p>

                        <h2 className="text-xl text-[#000000]">{activeProduct?.product?.productName}</h2>
                    </div>

                    {activeProduct?.product?.productDesc && (
                        <div className="relative">
                            <p
                                className={`"flex-grow flex-wrap font-normal text-[14px] leading-[26px] ${
                                    activeProduct?.product?.productDesc.split(' ').length > 50 && 'mr-14'
                                }"`}
                            >
                                {!displayFullDescription ? (
                                    activeProduct?.product?.productDesc.split(' ').length > 50 ? (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: formatDescription(
                                                    activeProduct?.product?.productDesc
                                                        .split(' ')
                                                        .slice(0, 50)
                                                        .join(' ') + '...'
                                                )
                                            }}
                                        />
                                    ) : (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: formatDescription(activeProduct?.product?.productDesc)
                                            }}
                                        />
                                    )
                                ) : (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: formatDescription(activeProduct?.product?.productDesc)
                                        }}
                                    />
                                )}
                            </p>

                            {activeProduct?.product?.productDesc.split(' ').length > 50 && (
                                <button
                                    onClick={handleDescriptionLength}
                                    className="hover:underline absolute bottom-0 right-0 font-semibold text-[14px] leading-[26px] text-[#1055EB]"
                                >
                                    {displayFullDescription ? 'see less' : 'see more'}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {colors.length > 0 && (
                    <div className="select-none flex flex-col gap-2">
                        <h2 className="font-semibold text-[14px] leading-[22px] text-[#666666]">Colors</h2>

                        <div className="flex flex-wrap gap-2">
                            {colors.map(colorVariant => {
                                const isActive = colorVariant.color === color;
                                return (
                                    <ProductColorBox
                                        key={colorVariant.id}
                                        color={colorVariant}
                                        isActive={isActive}
                                        id={colorVariant.id}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-2 select-none">
                    <h2 className="font-semibold text-[14px] leading-[22px] text-[#666666]">
                        {activeProduct?.product?.productCategory === 'Beauty & Personal Care' ? 'Volumes' : 'Size'}
                    </h2>

                    <div className="flex flex-wrap gap-2 font-semibold">
                        {sizeList && sizeList?.filter(s => s)?.length > 0 ? (
                            <>
                                {sizeList?.filter(s => s).map((sz, index) => {
                                    const isActive = sz === activeSize;
                                    return <ProductSizeBox key={index} size={sz} isActive={isActive} />;
                                })}
                            </>
                        ) : (
                            <p className='font-semibold text-sm text-red-500'>
                                No {activeProduct?.product?.productCategory === 'Beauty & Personal Care' ? 'Volumes' : 'Sizes'} available!
                            </p>
                        )}
                    </div>
                </div>

                {Object.keys(activeProduct || {}).length > 0 && (
                    <div className="flex items-center gap-2 select-none">
                        <p className="font-semibold text-2xl text-[#121212]">₹{sp}</p>
                        {discount > 0 && (
                            <>
                                <p className="line-through text-[18px] leading-[26px] text-[#666666]">₹{mrp}</p>
                                <p className="font-semibold text-[14px] leading-[26px] text-link">
                                    {discount}% off
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Specifications */}
            {Object.keys(activeProduct || {}).length > 0 && (
                <div className="select-none bg-[#FFFFFF] p-5 border border-[#EAEAEA] rounded-xl flex flex-col gap-3 shadow-[0px_0px_2px_0px_#00000022]">
                    <p className="font-semibold text-[18px] leading-6 text-[#121212]">Specifications</p>

                    {(() => {
                        const specs = activeProduct.product.productSpecs || {};

                        const orderedKeys = [
                            ...(['size', 'color'].filter((key) => key in specs)),
                            ...Object.keys(specs).filter((key) => !['size', 'color'].includes(key))
                        ];

                        return orderedKeys.map((key) => {
                            let value;
                            if (key === 'color') {
                                value = color;
                            } else if (key === 'size') {
                                value = activeSize;
                            } else {
                                value = specs[key];
                            }

                            if (!value) return null;

                            return (
                                <div className="flex flex-col gap-2" key={key}>
                                    <div className="w-full flex items-center gap-3 font-medium text-base">
                                        <h1 className="text-[#666666] w-1/2">
                                            {activeProduct?.product?.productCategory === 'Beauty & Personal Care'
                                                ? camelCaseToReadable(key === 'size' ? 'volume' : key)
                                                : camelCaseToReadable(key)}
                                        </h1>

                                        <div className="w-1/2 flex flex-wrap gap-2">
                                            {value
                                                ?.split(/\s*,\s*/)
                                                .filter(Boolean)
                                                .map((val, index) => (
                                                    <div
                                                        key={index}
                                                        className="size-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded-[30px] px-4 py-1 shadow-[0px_0px_4px_0px_#00000011]"
                                                    >
                                                        <p className="font-normal text-[14px] leading-6 text-[#121212] font-mulish">
                                                            {val}
                                                        </p>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            )}

            {/* Product Organisation */}
            {Object.keys(activeProduct || {}).length > 0 && (
                <div className="select-none bg-[#FFFFFF] p-5 border border-[#EAEAEA] rounded-xl flex flex-col gap-3 shadow-[0px_0px_2px_0px_#00000022]">
                    <p className="font-semibold text-[18px] leading-6 text-[#121212]">Product Organisation</p>

                    {categoriesList?.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <div className="w-full flex gap-3 font-medium text-base">
                                <h1 className="text-[#666666] w-1/2">Category</h1>

                                <div className="w-1/2 flex flex-wrap gap-2">
                                    {categoriesList?.map((item) => {
                                        return (
                                            <Link to={`/collections/categories/${item.id}`} key={item.id} className="size-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded-[30px] px-4 py-1 shadow-[0px_0px_4px_0px_#00000011]">
                                                <p className='font-normal text-[14px] leading-6 text-[#121212] font-mulish'>{item?.categoryName}</p>
                                            </Link>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {collectionsList?.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <div className="w-full flex gap-3 font-medium text-base">
                                <h1 className="text-[#666666] w-1/2">Collection</h1>

                                <div className="w-1/2 flex flex-wrap gap-2">
                                    {collectionsList.map((item) => {
                                        return (
                                            <Link to={`/collections/categories/${item.CategoryId}/collection/${item.id}`} key={item.id} className="size-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded-[30px] px-4 py-1 shadow-[0px_0px_4px_0px_#00000011]">
                                                <p className='font-normal text-[14px] leading-6 text-[#121212] font-mulish'>{item.CollectionName}</p>
                                            </Link>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {customCollectionsList?.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <div className="w-full flex gap-3 font-medium text-base">
                                <h1 className="text-[#666666] w-1/2">Custom Collection</h1>

                                <div className="w-1/2 flex flex-wrap gap-2">
                                    {customCollectionsList?.map((item) => {
                                        return (
                                            <Link to={`/collections/custom-collections/${item.id}`} key={item.id} className="size-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded-[30px] px-4 py-1 shadow-[0px_0px_4px_0px_#00000011]">
                                                <p className='font-normal text-[14px] leading-6 text-[#121212] font-mulish'>{item?.CollectionName}</p>
                                            </Link>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {customCollectionsList?.length === 0 &&
                        collectionsList?.length === 0 &&
                        categoriesList?.length === 0 && (
                            <h1 className="font-normal italic text-base text-[#666666]">
                                This product is not part of any collection or custom collection.
                            </h1>
                        )}
                </div>
            )}

            {/* Tags */}
            <div className="select-none bg-[#FFFFFF] p-5 border border-[#EAEAEA] rounded-xl flex flex-col gap-3 shadow-[0px_0px_2px_0px_#00000022]">
                <p className="font-semibold text-[18px] leading-6 text-[#121212]">Tags</p>

                {Object.keys(activeProduct || {}).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {activeProduct.product.Tag?.length > 0 ? (
                            activeProduct.product.Tag?.map((tag, index) => (
                                <div key={index} className="size-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded-[30px] px-4 py-1 shadow-[0px_0px_4px_0px_#00000011]">
                                <p className='font-normal text-[14px] leading-6 text-[#121212] font-mulish'>{tag}</p>
                            </div>
                            ))
                        ) : (
                            <h1 className="font-normal italic text-base text-[#666666]">
                                This product has no tags.
                            </h1>
                        )}
                    </div>
                )}
            </div>

            {/* Keywords */}
            <div className="select-none bg-[#FFFFFF] p-5 border border-[#EAEAEA] rounded-xl flex flex-col gap-3 shadow-[0px_0px_2px_0px_#00000022]">
                <p className="font-semibold text-[18px] leading-6 text-[#121212]">Keywords</p>

                {Object.keys(activeProduct || {}).length > 0 && activeProduct?.product?.keywords ? (
                    <div className="flex flex-wrap gap-2">
                        {activeProduct?.product?.keywords?.map((keyword, index) => (
                            <div key={index} className="size-fit border border-[#EAEAEA] bg-[#FFFFFF] rounded-[30px] px-4 py-1 shadow-[0px_0px_4px_0px_#00000011]">
                                <p className='font-normal text-[14px] leading-6 text-[#121212] font-mulish'>{keyword}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <h1 className="font-normal italic text-base text-[#666666]">
                        This product has no keywords.
                    </h1>
                )}
            </div>
        </div>
    );
}

export default ProductDetails;
